/**
 * SEO meta для каталога: вендоры, продукты, разделы, категории.
 * Формулы — plan-seo-vendors-solutions-meta-2026.md §2.1–3.5
 */
import { VENDOR_PAGE_SEO } from '../data/pageSeo';
import { buildProductCanonicalUrl } from './productCanonicalUrl';
import { catalogCategoryPath, catalogCategorySectionPath } from './catalogRoutes';
import { plainTextFromHtml } from './utils';

const BASE = 'https://antologia.tech';
export const SEO_PAYMENT_PHRASE = 'Оплата в рублях по безналичному расчёту';
export const SEO_BRAND_SUFFIX = ' | Антология';
export const SEO_TITLE_MAX = 70;
const PAYMENT_ANCHOR = SEO_PAYMENT_PHRASE;
const BRAND_SUFFIX = SEO_BRAND_SUFFIX;

export const SEO_LIMITS = {
  metaTitle: SEO_TITLE_MAX,
  metaDescription: 160,
};

function warnAutogenAnchors(context, meta) {
  if (process.env.NODE_ENV === 'production') return;
  const check = validateSeoAnchors(meta);
  if (!check.valid) {
    console.warn(`[catalogSeo] ${context}: anchor validation`, check.issues);
  }
}

/** §3.4 — период лицензии из массива licenses / calculator_config.licenses */
export function resolveLicensePeriod(licenses = []) {
  const list = Array.isArray(licenses) ? licenses : [];
  if (!list.length) return { period: 'индивидуальным', periodPhrase: '', mode: 'individual' };

  const kinds = list.map((lic) => classifyLicensePeriod(lic));
  const monthly = kinds.filter((k) => k === 'monthly').length;
  const yearly = kinds.filter((k) => k === 'yearly').length;
  const oneTime = kinds.filter((k) => k === 'one-time').length;

  if (monthly > 0 && yearly > 0) {
    return { period: 'месячным или годовым', periodPhrase: 'Тариф с месячным или годовым планом. ', mode: 'mixed' };
  }
  if (yearly >= monthly && yearly > 0) {
    return { period: 'годовым', periodPhrase: 'Тариф с годовым планом. ', mode: 'yearly' };
  }
  if (monthly > 0) {
    return { period: 'месячным', periodPhrase: 'Тариф с месячным планом. ', mode: 'monthly' };
  }
  if (oneTime > 0) {
    return { period: 'разовым', periodPhrase: 'Тариф с разовым планом. ', mode: 'one-time' };
  }
  return { period: 'индивидуальным', periodPhrase: '', mode: 'individual' };
}

function classifyLicensePeriod(lic) {
  const unit = String(lic?.price_unit || lic?.priceUnit || '').toLowerCase();
  const name = String(lic?.name || '').toLowerCase();
  const isMonthly = lic?.isMonthly === true || lic?.is_monthly === true;
  if (isMonthly || unit.includes('/мес') || unit.includes('мес')) return 'monthly';
  if (unit.includes('/год') || unit.includes('year') || unit.includes('annual') || name.includes('год')) {
    return 'yearly';
  }
  if (lic?.isMonthly === false && !unit.includes('/мес')) return 'one-time';
  return 'individual';
}

/** Мода периода среди продуктов вендора */
export function resolveVendorLicensePeriod(products = []) {
  const counts = { monthly: 0, yearly: 0, 'one-time': 0, individual: 0 };
  for (const p of products || []) {
    const licenses = p?.licenses || p?.calculator_config?.licenses || [];
    const { mode } = resolveLicensePeriod(licenses);
    counts[mode] = (counts[mode] || 0) + 1;
  }
  const top = Object.entries(counts).sort((a, b) => b[1] - a[1])[0]?.[0];
  if (!top || counts[top] === 0) return resolveLicensePeriod([]);
  if (top === 'mixed') return resolveLicensePeriod([{ isMonthly: true }, { price_unit: '/год' }]);
  const sample =
    top === 'monthly'
      ? { isMonthly: true }
      : top === 'yearly'
        ? { price_unit: '/год' }
        : top === 'one-time'
          ? { isMonthly: false }
          : {};
  return resolveLicensePeriod([sample]);
}

function vendorDisplayName(vendor) {
  return (vendor?.name || '').trim() || 'Вендор';
}

function vendorLower(vendor) {
  return vendorDisplayName(vendor).toLowerCase();
}

/** Имя продукта для SEO: name из БД, не сырой slug/uuid */
export function humanizeSlug(slug) {
  const raw = String(slug || '').trim();
  if (!raw || /^[0-9a-f]{8}-[0-9a-f-]{27,}$/i.test(raw)) return '';
  return raw
    .replace(/_/g, ' ')
    .replace(/-/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

export function resolveProductDisplayName(product) {
  const name = (product?.name || '').trim();
  if (name) return name;
  return humanizeSlug(product?.slug || product?.id);
}

function extractUtp(description, maxLen = 60) {
  const text = plainTextFromHtml(description || '', maxLen * 2).trim();
  if (!text) return '';
  const first = text.split(/[.!?]/)[0]?.trim() || text;
  const cleaned = first.replace(/лицензи[ияей]+/gi, '').trim();
  const slice = (cleaned || first).slice(0, maxLen).trim();
  return slice.endsWith('.') ? slice : `${slice}.`;
}

export function truncateDescription(text, max = SEO_LIMITS.metaDescription) {
  const raw = (text || '').replace(/\s+/g, ' ').trim();
  if (raw.length <= max) return raw;
  const paymentIdx = raw.indexOf(PAYMENT_ANCHOR);
  if (paymentIdx >= 0 && paymentIdx + PAYMENT_ANCHOR.length <= max) {
    return raw.slice(0, max - 1).trimEnd() + '…';
  }
  const cut = raw.slice(0, max - 1).trimEnd();
  if (cut.includes(PAYMENT_ANCHOR)) return `${cut}…`;
  const shortPayment = PAYMENT_ANCHOR.slice(0, 40);
  if (max > shortPayment.length + 20) {
    return `${cut.replace(/\.\s*$/, '')}. ${PAYMENT_ANCHOR}.`.slice(0, max);
  }
  return `${cut}…`;
}

export function validateSeoAnchors({ title = '', description = '', keywords = '' } = {}) {
  const issues = [];
  const combined = `${title} ${description} ${keywords}`.toLowerCase();
  const kwLower = (keywords || '').toLowerCase();
  if (!/лиценз/i.test(combined)) {
    issues.push('missing_license_anchor');
  }
  if (!/цен/i.test(combined) && !/выгодн/i.test(combined)) {
    issues.push('missing_price_anchor');
  }
  if (!description.includes(PAYMENT_ANCHOR)) {
    issues.push('missing_payment_anchor');
  }
  const licenseCount = (description.match(/лиценз/i) || []).length;
  if (licenseCount > 2) {
    issues.push('license_overuse_in_description');
  }
  const paymentCount = (description.match(/оплата в рублях по безналичному расчёту/gi) || []).length;
  if (paymentCount !== 1 && description.length > 0) {
    issues.push('payment_anchor_count');
  }
  if (keywords) {
    const licenseKw = (kwLower.match(/лиценз/g) || []).length;
    const priceKw = (kwLower.match(/цен/g) || []).length;
    if (licenseKw < 3) issues.push('keywords_license_count');
    if (priceKw < 2) issues.push('keywords_price_count');
    if (!/оплата в рублях|безналичн/i.test(kwLower)) {
      issues.push('keywords_payment_count');
    }
  }
  const valid = issues.length === 0;
  return { valid, issues, ok: valid, errors: issues };
}

export function truncateMeta(text, max = SEO_LIMITS.metaDescription) {
  return truncateDescription(text, max);
}

function ensureProductTitleAnchors(title, fallbackTitle) {
  const t = (title || '').trim();
  if (!t) return fallbackTitle;
  if (/лиценз/i.test(t) && (/цен/i.test(t) || /выгодн/i.test(t))) return t;
  return fallbackTitle;
}

function ensureProductDescriptionAnchors(description, product, vendor, period, brief, pricePhrase) {
  const d = (description || '').trim();
  if (!d) return null;
  if (d.includes(PAYMENT_ANCHOR)) return d;
  const tail = ` ${PAYMENT_ANCHOR} для юридических лиц. Закрывающие документы, поставка на аккаунт клиента.`;
  return truncateDescription(`${d}${tail}`);
}

function buildVendorKeywords(vendor) {
  const v = vendorLower(vendor);
  const V = vendorDisplayName(vendor);
  return [
    `лицензии ${v}`,
    `лицензия ${v}`,
    `цена ${v}`,
    `${v} цена лицензии`,
    `купить лицензию ${v}`,
    `${v} для юрлиц`,
    `оплата в рублях ${v}`,
    'безналичный расчёт лицензия',
    `поставка ${V}`,
  ].join(', ');
}

export function buildVendorSeo(vendor, products = []) {
  const V = vendorDisplayName(vendor);
  const slug = (vendor?.slug || '').trim();
  const { periodPhrase } = resolveVendorLicensePeriod(products);
  const utp = extractUtp(vendor?.description || vendor?.full_description);

  let title;
  if (vendor?.seo_primary_query) {
    title = `${vendor.seo_primary_query} — лицензии по выгодной цене${BRAND_SUFFIX}`;
  } else if (V.length > 18) {
    title = `Лицензия ${V}: цена для юрлиц${BRAND_SUFFIX}`;
  } else {
    title = `Лицензии ${V} по выгодной цене${BRAND_SUFFIX}`;
  }

  let description = `Лицензии ${V} для юридических лиц и ИП.`;
  if (utp) description += ` ${utp}`;
  description += ` ${PAYMENT_ANCHOR}.`;
  if (periodPhrase) description += ` ${periodPhrase}`;
  description += 'Закрывающие документы, поставка лицензии на аккаунт клиента.';

  return {
    title: title.slice(0, SEO_TITLE_MAX + BRAND_SUFFIX.length),
    description: truncateDescription(description),
    keywords: vendor?.meta_keywords?.trim() || buildVendorKeywords(vendor),
    canonical: slug ? `${BASE}/solutions/${encodeURIComponent(slug)}` : `${BASE}/solutions`,
    h1: `Лицензии ${V}`,
  };
}

function buildProductAutogenTitle(P, V, period) {
  const longName = P.length > 35;
  return longName
    ? `Цена лицензии ${P} (${V})${BRAND_SUFFIX}`
    : `Лицензия ${P} (${V}) — цена и ${period} план${BRAND_SUFFIX}`;
}

function buildProductAutogenDescription(P, V, period, brief, pricePhrase) {
  let description = `Лицензия ${P} (${V}): ${period} план. ${PAYMENT_ANCHOR} для юридических лиц.`;
  if (brief) description += ` ${brief}.`;
  description += ` ${pricePhrase} Закрывающие документы, поставка на аккаунт клиента.`;
  return description;
}

function formatProductPrice(product) {
  const price = product?.price;
  if (!price || String(price).toLowerCase().includes('запрос')) {
    return 'Цена по запросу.';
  }
  const num = Number(String(price).replace(/[^\d.]/g, ''));
  if (Number.isFinite(num) && num > 0) {
    return `Цена от ${Math.round(num).toLocaleString('ru-RU')} ₽.`;
  }
  return `Цена: ${price}.`;
}

function buildProductKeywords(product, vendor) {
  const P = (product?.name || '').trim();
  const V = vendorDisplayName(vendor);
  const tag = (product?.tags?.[0] || product?.category || 'решение').toString();
  return [
    `${P} лицензия`,
    `цена ${P}`,
    `лицензия ${P} ${V}`,
    `купить лицензию ${P}`,
    `${P} цена для юрлиц`,
    `оплата в рублях ${P}`,
    'безналичный расчёт',
    `${tag} лицензия цена`,
  ].join(', ');
}

export function buildProductSeo(product, vendor, options = {}) {
  const P = resolveProductDisplayName(product) || 'Решение';
  const V = vendorDisplayName(vendor);
  const licenses = product?.licenses || product?.calculator_config?.licenses || [];
  const { period } = resolveLicensePeriod(licenses);
  const brief = plainTextFromHtml(product?.short_description || product?.full_description || '', 80);
  const pricePhrase = formatProductPrice(product);

  const titleFromDb = product?.meta_title?.trim();
  const descFromDb = product?.meta_description?.trim();
  const autogenTitle = buildProductAutogenTitle(P, V, period);
  const autogenDescription = buildProductAutogenDescription(P, V, period, brief, pricePhrase);

  let title = ensureProductTitleAnchors(titleFromDb, autogenTitle);

  let description = descFromDb
    ? ensureProductDescriptionAnchors(descFromDb, product, vendor, period, brief, pricePhrase) || autogenDescription
    : autogenDescription;

  const keywords =
    product?.ai_keywords?.trim() ||
    product?.meta_keywords?.trim() ||
    buildProductKeywords(product, vendor);

  const canonical = buildProductCanonicalUrl(BASE, product, options);

  const result = {
    title: title.slice(0, SEO_TITLE_MAX + BRAND_SUFFIX.length),
    description: truncateDescription(description),
    keywords,
    canonical,
    heroSubtitle: 'Оплата в рублях для юрлиц.',
  };
  warnAutogenAnchors(`product:${P}`, result);
  return result;
}

export function buildSectionSeo(section, category) {
  const sectionName = (section?.name || '').trim();
  const categoryName = (category?.name || category?.id || '').trim();
  const categoryId = (category?.id || section?.category_id || '').trim();
  const sectionSlug = (section?.slug || '').trim();

  if (section?.meta_title?.trim()) {
    return {
      title: section.meta_title.trim(),
      description: truncateDescription(
        section.meta_description?.trim() ||
          `Лицензии в разделе «${sectionName}». ${PAYMENT_ANCHOR} для юридических лиц. Выгодные цены, закрывающие документы.`,
      ),
      keywords:
        section.meta_keywords?.trim() ||
        `лицензии ${sectionName}, цена ${sectionName}, ${categoryName} лицензия`,
      canonical:
        categoryId && sectionSlug
          ? `${BASE}${catalogCategorySectionPath(categoryId, sectionSlug)}`
          : `${BASE}/solutions`,
    };
  }

  const title = `Лицензии ${sectionName} — цена для бизнеса${BRAND_SUFFIX}`;
  const description = truncateDescription(
    `Лицензии в разделе «${sectionName}» (${categoryName}) для юридических лиц и ИП. ${PAYMENT_ANCHOR}. Выгодная цена, закрывающие документы, поставка на аккаунт клиента.`,
  );
  return {
    title,
    description,
    keywords: `лицензии ${sectionName}, цена ${sectionName}, ${categoryName} лицензия, оплата в рублях`,
    canonical:
      categoryId && sectionSlug
        ? `${BASE}${catalogCategorySectionPath(categoryId, sectionSlug)}`
        : `${BASE}/solutions`,
  };
}

export function buildCategorySeo(category) {
  const name = (category?.name || category?.id || '').trim();
  const categoryId = (category?.id || '').trim();

  if (category?.meta_title?.trim()) {
    return {
      title: category.meta_title.trim(),
      description: truncateDescription(
        category.meta_description?.trim() ||
          `Лицензии в категории «${name}». ${PAYMENT_ANCHOR} для юридических лиц. Каталог решений с выгодными ценами.`,
      ),
      keywords: category.meta_keywords?.trim() || `лицензии ${name}, цена ${name}, каталог ${name}`,
      canonical: categoryId ? `${BASE}${catalogCategoryPath(categoryId)}` : `${BASE}/solutions`,
    };
  }

  return {
    title: `Лицензии ${name} — каталог и цены${BRAND_SUFFIX}`,
    description: truncateDescription(
      `Каталог лицензий в категории «${name}» для юридических лиц и ИП. ${PAYMENT_ANCHOR}. Выгодная цена, подбор и поставка решений.`,
    ),
    keywords: `лицензии ${name}, цена ${name}, каталог ${name}, оплата в рублях`,
    canonical: categoryId ? `${BASE}${catalogCategoryPath(categoryId)}` : `${BASE}/solutions`,
  };
}

/**
 * Единая точка для CatalogPage: вендор / раздел / категория / корень каталога.
 */
export function buildCatalogPageSeo({
  vendor = null,
  vendorProducts = [],
  category = null,
  section = null,
  pathname = '/solutions',
} = {}) {
  if (vendor?.slug) {
    const generated = buildVendorSeo(vendor, vendorProducts);
    const override = VENDOR_PAGE_SEO[vendor.slug];
    const hasDbMeta = Boolean(vendor.meta_title?.trim() || vendor.meta_description?.trim());

    if (hasDbMeta) {
      const result = {
        title: vendor.meta_title?.trim() || generated.title,
        description: truncateDescription(
          vendor.meta_description?.trim() || generated.description,
        ),
        keywords: vendor.meta_keywords?.trim() || generated.keywords,
        canonical: generated.canonical,
        h1: generated.h1,
      };
      warnAutogenAnchors(`vendor:${vendor.slug}`, result);
      return result;
    }

    if (override) {
      const result = {
        title: override.title || generated.title,
        description: override.description || generated.description,
        keywords: override.keywords || generated.keywords,
        canonical: override.canonical || generated.canonical,
        h1: generated.h1,
      };
      warnAutogenAnchors(`vendor:${vendor.slug}`, result);
      return result;
    }

    warnAutogenAnchors(`vendor:${vendor.slug}`, generated);
    return generated;
  }

  const vendorPathMatch = String(pathname).match(/^\/solutions\/([^/]+)\/?$/);
  if (vendorPathMatch) {
    const slug = decodeURIComponent(vendorPathMatch[1]);
    const stub = { name: slug.replace(/-/g, ' '), slug };
    const generated = buildVendorSeo(stub, vendorProducts);
    const override = VENDOR_PAGE_SEO[slug];
    if (override) {
      return {
        title: override.title || generated.title,
        description: override.description || generated.description,
        keywords: override.keywords || generated.keywords,
        canonical: override.canonical || generated.canonical,
        h1: generated.h1,
      };
    }
    return generated;
  }

  if (section && category) {
    return buildSectionSeo(section, category);
  }

  if (category && category.id && category.id !== 'all') {
    return buildCategorySeo(category);
  }

  return {
    title: 'Каталог лицензий: российские и зарубежные IT и AI | Антология',
    description:
      'Каталог лицензий на российское и зарубежное ПО и AI: подбор, поставка на аккаунт клиента, on-premise и облако, внедрение и техподдержка.',
    canonical: `${BASE}/solutions`,
    keywords: 'каталог лицензий, цена лицензии, оплата в рублях',
  };
}
